// CameraIndicator Component - 2ECOMP Fleet Management
// Small indicator showing camera availability and status

import React, { useMemo } from 'react';
import {
  Box,
  Tooltip,
  Badge,
  Typography,
  Chip,
  Stack,
} from '@mui/material';
import { styled, alpha, keyframes } from '@mui/material/styles';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CameraAltIcon from '@mui/icons-material/CameraAlt';

import { useDeviceCameraStatus } from '../hooks/useCameraStatus';
import { CHANNEL_NAMES } from '../utils/cameraConstants';

// Pulse animation for live indicator
const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

// Styled Components
const IndicatorBadge = styled(Badge)(({ theme, live }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: live ? theme.palette.success.main : theme.palette.grey[500],
    color: theme.palette.success.main,
    boxShadow: live ? `0 0 0 2px ${theme.palette.background.paper}` : 'none',
    animation: live ? `${pulse} 2s ease-in-out infinite` : 'none',
    '&::after': live ? {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: 'ripple 1.2s infinite ease-in-out',
      border: '1px solid currentColor',
      content: '""',
    } : {},
  },
  '@keyframes ripple': {
    '0%': {
      transform: 'scale(.8)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(2.4)',
      opacity: 0,
    },
  },
}));

const LiveDot = styled(FiberManualRecordIcon)(({ theme }) => ({
  fontSize: 8,
  color: theme.palette.success.main,
  animation: `${pulse} 2s ease-in-out infinite`,
}));

const CompactIndicator = styled(Box)(({ theme, active }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  borderRadius: '50%',
  backgroundColor: active
    ? alpha(theme.palette.success.main, 0.1)
    : alpha(theme.palette.grey[500], 0.1),
  color: active ? theme.palette.success.main : theme.palette.grey[500],
}));

/**
 * CameraIndicator - Shows camera availability status
 *
 * @param {Object} props
 * @param {string|number} props.deviceId - Device ID to check
 * @param {string} props.variant - 'icon' | 'badge' | 'chip' | 'compact' | 'detailed'
 * @param {string} props.size - 'small' | 'medium' | 'large'
 * @param {boolean} props.showTooltip - Show tooltip with details
 * @param {function} props.onClick - Click handler
 * @param {Object} props.sx - Additional MUI sx props
 */
const CameraIndicator = ({
  deviceId,
  variant = 'icon',
  size = 'medium',
  showTooltip = true,
  onClick = null,
  sx = {},
}) => {
  // Get camera status for device
  const { hasCamera, activeChannels, channelCount, isLoading } = useDeviceCameraStatus(deviceId);

  // Size configurations
  const sizeConfig = useMemo(() => ({
    small: { icon: 16, badge: 6, chip: 'small' },
    medium: { icon: 20, badge: 8, chip: 'small' },
    large: { icon: 24, badge: 10, chip: 'medium' },
  }), []);

  const config = sizeConfig[size] || sizeConfig.medium;

  // Generate tooltip content
  const tooltipContent = useMemo(() => {
    if (!hasCamera) {
      return 'Sem câmera ativa';
    }

    const channelNames = activeChannels
      .map(ch => CHANNEL_NAMES[ch] || `Canal ${ch}`)
      .join(', ');

    return `${channelCount} câmera${channelCount > 1 ? 's' : ''} ativa${channelCount > 1 ? 's' : ''}: ${channelNames}`;
  }, [hasCamera, activeChannels, channelCount]);

  // Render based on variant
  const renderIndicator = () => {
    switch (variant) {
      case 'badge':
        return (
          <IndicatorBadge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            variant="dot"
            live={hasCamera ? 1 : 0}
          >
            <VideocamIcon
              sx={{
                fontSize: config.icon,
                color: hasCamera ? 'success.main' : 'text.disabled',
              }}
            />
          </IndicatorBadge>
        );

      case 'chip':
        return (
          <Chip
            icon={<VideocamIcon />}
            label={hasCamera ? `${channelCount} CH` : 'OFF'}
            size={config.chip}
            color={hasCamera ? 'success' : 'default'}
            variant={hasCamera ? 'filled' : 'outlined'}
            onClick={onClick}
            sx={{
              cursor: onClick ? 'pointer' : 'default',
              '& .MuiChip-icon': {
                fontSize: config.icon - 2,
              },
            }}
          />
        );

      case 'compact':
        return (
          <CompactIndicator active={hasCamera}>
            <VideocamIcon sx={{ fontSize: config.icon - 4 }} />
          </CompactIndicator>
        );

      case 'detailed':
        return (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                borderRadius: 1,
                backgroundColor: (t) => hasCamera
                  ? alpha(t.palette.success.main, 0.1)
                  : alpha(t.palette.grey[500], 0.1),
              }}
            >
              <VideocamIcon
                sx={{
                  fontSize: config.icon,
                  color: hasCamera ? 'success.main' : 'text.disabled',
                }}
              />
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: hasCamera ? 'success.main' : 'text.disabled',
                  display: 'block',
                  lineHeight: 1.2,
                }}
              >
                {hasCamera ? 'AO VIVO' : 'OFFLINE'}
              </Typography>
              {hasCamera && (
                <Typography
                  variant="caption"
                  sx={{ fontSize: '0.65rem', color: 'text.secondary', lineHeight: 1 }}
                >
                  {channelCount} canal{channelCount > 1 ? 'is' : ''}
                </Typography>
              )}
            </Box>
          </Stack>
        );

      case 'icon':
      default:
        return (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              cursor: onClick ? 'pointer' : 'default',
            }}
            onClick={onClick}
          >
            {hasCamera ? (
              <Stack direction="row" spacing={0.25} alignItems="center">
                <VideocamIcon
                  sx={{
                    fontSize: config.icon,
                    color: 'success.main',
                  }}
                />
                <LiveDot />
              </Stack>
            ) : (
              <VideocamOffIcon
                sx={{
                  fontSize: config.icon,
                  color: 'text.disabled',
                }}
              />
            )}
          </Box>
        );
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Box sx={{ display: 'inline-flex', opacity: 0.5, ...sx }}>
        <VideocamIcon sx={{ fontSize: config.icon, color: 'text.disabled' }} />
      </Box>
    );
  }

  // Wrap with tooltip if enabled
  if (showTooltip) {
    return (
      <Tooltip title={tooltipContent} arrow>
        <Box sx={{ display: 'inline-flex', ...sx }}>
          {renderIndicator()}
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box sx={{ display: 'inline-flex', ...sx }}>
      {renderIndicator()}
    </Box>
  );
};

/**
 * CameraStatusBadge - Badge overlay for other elements
 */
export const CameraStatusBadge = ({ deviceId, children }) => {
  const { hasCamera } = useDeviceCameraStatus(deviceId);

  return (
    <IndicatorBadge
      overlap="circular"
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      variant="dot"
      live={hasCamera ? 1 : 0}
      invisible={!hasCamera}
    >
      {children}
    </IndicatorBadge>
  );
};

/**
 * CameraCountChip - Shows count of cameras for a fleet
 */
export const CameraCountChip = ({ count, activeCount = 0, onClick }) => {
  return (
    <Chip
      icon={<CameraAltIcon />}
      label={activeCount > 0 ? `${activeCount}/${count}` : count}
      size="small"
      color={activeCount > 0 ? 'success' : 'default'}
      variant="outlined"
      onClick={onClick}
      sx={{ cursor: onClick ? 'pointer' : 'default' }}
    />
  );
};

export default CameraIndicator;
